import type { AutoWithdrawalNetworkCode } from '@shared/api/contracts';
import { keccak256 } from './keccak256';

export type CryptoAddressValidationErrorCode =
  | 'REQUIRED'
  | 'INVALID_ADDRESS_FORMAT'
  | 'INVALID_ADDRESS_CHECKSUM'
  | 'NETWORK_MISMATCH';

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

const base58Decode = (str: string): Uint8Array => {
  const base = BASE58_ALPHABET.length;
  let num = 0n;
  for (let i = 0; i < str.length; i += 1) {
    const char = str[i];
    const index = BASE58_ALPHABET.indexOf(char);
    if (index === -1) throw new Error('Invalid Base58 character');
    num = num * BigInt(base) + BigInt(index);
  }

  const bytes: number[] = [];
  while (num > 0n) {
    bytes.unshift(Number(num % 256n));
    num /= 256n;
  }

  const leadingZeros = str.match(/^1*/)?.[0].length ?? 0;
  for (let i = 0; i < leadingZeros; i += 1) {
    bytes.unshift(0);
  }

  return new Uint8Array(bytes);
};

const sha256 = async (data: Uint8Array): Promise<Uint8Array> => {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data as BufferSource);
    return new Uint8Array(hashBuffer);
  }
  throw new Error('SHA256 not available');
};

const isAscii = (value: string): boolean => {
  for (let index = 0; index < value.length; index += 1) {
    if (value.charCodeAt(index) > 0x7f) return false;
  }
  return true;
};

const validateTronAddress = async (address: string) => {
  try {
    const decoded = base58Decode(address);
    if (decoded.length !== 25 || decoded[0] !== 0x41) {
      return { isValid: false as const, errorCode: 'INVALID_ADDRESS_FORMAT' as const };
    }
    const payload = decoded.slice(0, 21);
    const checksum = decoded.slice(21);
    const hash1 = await sha256(payload);
    const hash2 = await sha256(hash1);
    const expectedChecksum = hash2.slice(0, 4);
    if (checksum.every((byte, i) => byte === expectedChecksum[i])) {
      return { isValid: true as const };
    }
    return { isValid: false as const, errorCode: 'INVALID_ADDRESS_CHECKSUM' as const };
  } catch {
    return { isValid: false as const, errorCode: 'INVALID_ADDRESS_FORMAT' as const };
  }
};

const formatError = { isValid: false as const, errorCode: 'INVALID_ADDRESS_FORMAT' as const };
const checksumError = { isValid: false as const, errorCode: 'INVALID_ADDRESS_CHECKSUM' as const };
const validAddress = { isValid: true as const };

const validateEvmAddress = (address: string) => {
  if (!/^0x[a-fA-F0-9]{40}$/.test(address)) return formatError;
  if (address.toLowerCase() === '0x0000000000000000000000000000000000000000') return formatError;

  const body = address.slice(2);
  if (body === body.toLowerCase() || body === body.toUpperCase()) return validAddress;

  const hash = keccak256(new TextEncoder().encode(body.toLowerCase()));
  for (let index = 0; index < body.length; index += 1) {
    const nibble = (hash[index >> 1] >> (index % 2 === 0 ? 4 : 0)) & 0x0f;
    const char = body[index];
    const expected = nibble >= 8 ? char.toUpperCase() : char.toLowerCase();
    if (char !== expected) return checksumError;
  }

  return validAddress;
};

const BECH32_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

const bech32Polymod = (values: number[]): number => {
  const generators = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let checksum = 1;
  for (const value of values) {
    const top = checksum >> 25;
    checksum = ((checksum & 0x1ffffff) << 5) ^ value;
    for (let index = 0; index < 5; index += 1) {
      if ((top >> index) & 1) checksum ^= generators[index];
    }
  }
  return checksum >>> 0;
};

const verifyBech32 = (address: string, hrp: string): boolean => {
  const mixedCase = address !== address.toLowerCase() && address !== address.toUpperCase();
  if (mixedCase) return false;
  const lower = address.toLowerCase();
  const separator = lower.lastIndexOf('1');
  if (separator < 1 || separator + 7 > lower.length || lower.slice(0, separator) !== hrp) return false;
  const data: number[] = [];
  for (const char of lower.slice(separator + 1)) {
    const value = BECH32_CHARSET.indexOf(char);
    if (value === -1) return false;
    data.push(value);
  }
  const expanded: number[] = [];
  for (const char of hrp) expanded.push(char.charCodeAt(0) >> 5);
  expanded.push(0);
  for (const char of hrp) expanded.push(char.charCodeAt(0) & 31);
  const polymod = bech32Polymod(expanded.concat(data));
  return polymod === 1 || polymod === 0x2bc830a3;
};

const validateBase58Check = async (address: string, versions: number[]) => {
  try {
    const decoded = base58Decode(address);
    if (decoded.length !== 25 || !versions.includes(decoded[0])) return formatError;
    const payload = decoded.slice(0, 21);
    const checksum = decoded.slice(21);
    const hash = (await sha256(await sha256(payload))).slice(0, 4);
    if (checksum.every((byte, index) => byte === hash[index])) return validAddress;
    return checksumError;
  } catch {
    return formatError;
  }
};

const validateBitcoinAddress = async (address: string) => {
  if (/^bc1/i.test(address)) {
    return verifyBech32(address, 'bc') ? validAddress : checksumError;
  }
  if (/^[13]/.test(address)) return validateBase58Check(address, [0x00, 0x05]);
  return formatError;
};

const validateLitecoinAddress = async (address: string) => {
  if (/^ltc1/i.test(address)) {
    return verifyBech32(address, 'ltc') ? validAddress : checksumError;
  }
  if (/^[LM3]/.test(address)) return validateBase58Check(address, [0x30, 0x32, 0x05]);
  return formatError;
};

export type AddressFamily = 'TRON' | 'EVM' | 'BTC' | 'LTC';

export const detectAddressFamily = (address: string): AddressFamily | null => {
  const trimmed = address.trim();
  if (/^0x[a-fA-F0-9]{40}$/.test(trimmed)) return 'EVM';
  if (/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(trimmed)) return 'TRON';
  if (/^(bc1|1)/i.test(trimmed)) return 'BTC';
  if (/^(ltc1|[LM])/i.test(trimmed)) return 'LTC';
  return null;
};

const networkFamily = (network: string): AddressFamily | null => {
  if (network === 'TRC20') return 'TRON';
  if (network === 'ERC20' || network === 'BEP20') return 'EVM';
  if (network === 'BTC') return 'BTC';
  if (network === 'LTC') return 'LTC';
  return null;
};

export const validateCryptoAddress = async (
  address: string,
  networkCode: AutoWithdrawalNetworkCode | string,
) => {
  if (!address?.trim()) {
    return { isValid: false as const, errorCode: 'REQUIRED' as const };
  }

  const trimmed = address.trim();
  if (trimmed.length > 128) {
    return { isValid: false as const, errorCode: 'INVALID_ADDRESS_FORMAT' as const };
  }

  if (!isAscii(trimmed) || /\s/.test(trimmed)) {
    return { isValid: false as const, errorCode: 'INVALID_ADDRESS_FORMAT' as const };
  }

  const network = networkCode.toUpperCase().replace(/-/g, '');
  const family = detectAddressFamily(trimmed);
  const expected = networkFamily(network);
  if (family && expected && family !== expected) {
    const strongShape = family === 'EVM' || family === 'TRON' || /^(bc1|ltc1)/i.test(trimmed);
    if (strongShape) {
      return { isValid: false as const, errorCode: 'NETWORK_MISMATCH' as const };
    }
    const other = family === 'BTC' ? await validateBitcoinAddress(trimmed) : await validateLitecoinAddress(trimmed);
    if (other.isValid) {
      return { isValid: false as const, errorCode: 'NETWORK_MISMATCH' as const };
    }
  }

  if (network === 'TRC20') return validateTronAddress(trimmed);
  if (network === 'ERC20' || network === 'BEP20') return validateEvmAddress(trimmed);
  if (network === 'BTC') return validateBitcoinAddress(trimmed);
  if (network === 'LTC') return validateLitecoinAddress(trimmed);

  return { isValid: false as const, errorCode: 'INVALID_ADDRESS_FORMAT' as const };
};
